
'use client';

/* eslint-disable @next/next/no-img-element -- Exact Design-export dimensions and S3 sources are required for visual parity; every image is lazy and async-decoded. */

import { useEffect, useMemo, useState } from 'react';
import styles from './ViewstationMobileGuide.module.css';

// Raw images preserve the Design export's exact intrinsic rendering; all are deferred below.

const VIDEOS = {
  ui: { id: 'oD7KP07kTFA', kicker: 'Platform overview', title: 'General UI' },
  search: { id: 'bFZXZ9b2ZyU', kicker: 'Investigation', title: 'People, Vehicle & Color Search' },
  trace: { id: 'dLCfuqtKws8', kicker: 'Investigation', title: 'TraceSearch in VSM' },
  investigation: { id: 'GRc5wHp2k-U', kicker: 'Investigation', title: 'SCW Viewstation Mobile ' + 'investigation tools' },
} as const;

type VideoKey = keyof typeof VIDEOS;

const HOME_SCREEN_INTRO = 'The Home screen is the default landing page. Viewstation Mobile supports Simple and Standard layouts; applications are hidden in Simple mode.';

export function ViewstationMobileGuide() {
  const [activeVideo, setActiveVideo] = useState<VideoKey | null>(null);
  const [openSections, setOpenSections] = useState(() => new Set(['my_system', 'motion']));
  const video = useMemo(() => activeVideo ? { ...VIDEOS[activeVideo], src: `https://www.youtube.com/embed/${VIDEOS[activeVideo].id}?list=PLDp0wmTrVoZA&autoplay=1&rel=0` } : null, [activeVideo]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setActiveVideo(null); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);
  const toggleSection = (key: string) => setOpenSections((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
  <article className={styles.page} data-screen-label={'Viewstation Mobile ' + 'setup guide'} data-copy-source="ahead-of-repo" data-copy-reviewed="2026-08-28">
    <section className={styles.s18} id="top">
      <div className={styles.s17}>
        <div>
          <div className={styles.s0}>
            {"SCW support guide"}
          </div>
          <h1 className={styles.s1}>
            {"SCW Viewstation Mobile: tour the app, then set it up"}
          </h1>
          <p className={styles.s2}>
            {"Start with three short videos: a walkthrough of the interface, People, Vehicle & Color Search, and TraceSearch - to see what the app does on a real system."}
          </p>
          <p className={styles.s3}>
            {"Then follow the step-by-step guide below: download the app, create your account, add your SCW NVR, watch live and recorded video, configure alerts, share access, manage teams, and adjust supported NVR and camera settings from your mobile device."}
          </p>
          <div className={styles.s7}>
            <a className={styles.s6} href="https://apps.apple.com/us/app/scw-viewstation-mobile/id6747735805">
              <span className={styles.s4}>
                {"Download on the"}
              </span>
              <span className={styles.s5}>
                {"App Store"}
              </span>
            </a>
            <a className={styles.s6} href="https://play.google.com/store/apps/details?id=com.SCWViewstationMobile.mobile&amp;hl=en_US">
              <span className={styles.s4}>
                {"Get it on"}
              </span>
              <span className={styles.s5}>
                {"Google Play"}
              </span>
            </a>
          </div>
          <div className={styles.s8}>
            <a href="#videos" className={styles.buttonPrimary}>
              {"Watch the overview"}
            </a>
            <a href="#setup" className={styles.buttonSecondary}>
              {"Start setup"}
            </a>
            <a href="#advanced" className={styles.buttonOutline}>
              {"Jump to settings"}
            </a>
          </div>
        </div>
        <div className={styles.s16}>
          <div className={styles.s9}>
            {"On this page"}
          </div>
          <div className={styles.s15}>
            <a className={styles.s11} href="#videos">
              <span className={styles.s10}>
                {"▸"}
              </span>
              {" Video overview"}
            </a>
            <a className={styles.s13} href="#setup">
              <span className={styles.s12}>
                {"1"}
              </span>
              {" Download & create account"}
            </a>
            <a className={styles.s11} href="#add-system">
              <span className={styles.s12}>
                {"2"}
              </span>
              {" Add your SCW NVR"}
            </a>
            <a className={styles.s11} href="#home-screen">
              <span className={styles.s12}>
                {"3"}
              </span>
              {" Learn the Home screen"}
            </a>
            <a className={styles.s11} href="#live-view">
              <span className={styles.s12}>
                {"4"}
              </span>
              {" Live View and playback"}
            </a>
            <a className={styles.s11} href="#notifications">
              <span className={styles.s12}>
                {"5"}
              </span>
              {" Alarm notifications"}
            </a>
            <a className={styles.s11} href="#sharing">
              <span className={styles.s12}>
                {"6"}
              </span>
              {" Share access"}
            </a>
            <a className={styles.s11} href="#teams">
              <span className={styles.s12}>
                {"7"}
              </span>
              {" Manage teams"}
            </a>
            <a className={styles.s11} href="#nvr-settings">
              <span className={styles.s12}>
                {"8"}
              </span>
              {" NVR configuration"}
            </a>
            <a className={styles.s11} href="#camera-settings">
              <span className={styles.s12}>
                {"9"}
              </span>
              {" Camera configuration"}
            </a>
            <a className={styles.s14} href="#faq">
              {"FAQ"}
            </a>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s34} id="videos">
      <div className={styles.s19}>
        {"See it in action"}
      </div>
      <h2 className={styles.s20}>
        {"Platform overview and key features"}
      </h2>
      <p className={styles.s21}>
        {"Three short videos covering the interface and the investigation tools that make Viewstation Mobile different. Each one opens in a full-screen player."}
      </p>
      <div className={styles.s33}>
        <div className={styles.s32}>
          <div className={styles.s27}>
            <button className={styles.s26} type="button" aria-label="Play: General UI walkthrough" data-no-cms-button onClick={() => setActiveVideo('ui')}>
              <img loading="lazy" decoding="async" className={styles.s22} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/66fd09fd-general-ui-walkthrough-video-thumbnail.jpg" data-scw-image="general-ui-walkthrough-video-thumbnail" alt="General UI walkthrough video thumbnail" />
              <span className={styles.s25}>
                <span className={styles.s24}>
                  <span className={styles.s23}>

                  </span>
                </span>
              </span>
            </button>
          </div>
          <div className={styles.s31}>
            <div className={styles.s28}>
              {"Platform overview"}
            </div>
            <h3 className={styles.s29}>
              {"General UI"}
            </h3>
            <p className={styles.s30}>
              {"A walkthrough of the Home screen, layouts, live view, and playback controls. See how the app is organized before you start configuring it."}
            </p>
          </div>
        </div>
        <div className={styles.s32}>
          <div className={styles.s27}>
            <button className={styles.s26} type="button" aria-label="Play: People, Vehicle and Color Search" data-no-cms-button onClick={() => setActiveVideo('search')}>
              <img loading="lazy" decoding="async" className={styles.s22} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/15eaec6d-people-vehicle-and-color-search-video-thumbnail.jpg" data-scw-image="people-vehicle-and-color-search-video-thumbnail" alt="People, Vehicle and Color Search video thumbnail" />
              <span className={styles.s25}>
                <span className={styles.s24}>
                  <span className={styles.s23}>

                  </span>
                </span>
              </span>
            </button>
          </div>
          <div className={styles.s31}>
            <div className={styles.s28}>
              {"Investigation"}
            </div>
            <h3 className={styles.s29}>
              {"People, Vehicle & Color Search"}
            </h3>
            <p className={styles.s30}>
              {"Filter recorded footage by person, vehicle, or color instead of scrubbing a timeline. The fastest way to find an incident."}
            </p>
          </div>
        </div>
        <div className={styles.s32}>
          <div className={styles.s27}>
            <button className={styles.s26} type="button" aria-label="Play: TraceSearch in Viewstation Mobile" data-no-cms-button onClick={() => setActiveVideo('trace')}>
              <img loading="lazy" decoding="async" className={styles.s22} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/83a88ab8-tracesearch-in-viewstation-mobile-video-thumbnail.jpg" data-scw-image="tracesearch-in-viewstation-mobile-video-thumbnail" alt="TraceSearch in Viewstation Mobile video thumbnail" />
              <span className={styles.s25}>
                <span className={styles.s24}>
                  <span className={styles.s23}>

                  </span>
                </span>
              </span>
            </button>
          </div>
          <div className={styles.s31}>
            <div className={styles.s28}>
              {"Investigation"}
            </div>
            <h3 className={styles.s29}>
              {"TraceSearch in VSM"}
            </h3>
            <p className={styles.s30}>
              {"Follow a person or vehicle from camera to camera across the system to reconstruct where they went and when."}
            </p>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s59} id="setup">
      <div className={styles.s35}>
        {"Getting started"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"1"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Download the app and create your account"}
          </h2>
          <p className={styles.s38}>
            {"SCW Viewstation Mobile is available for iPhone/iOS and Android. On first launch, allow the requested app permissions so features such as scanning, saved media, microphone access, and notifications can work correctly."}
          </p>
          <div className={styles.s46}>
            <div>
              <h3 className={styles.s39}>
                {"Download SCW Viewstation Mobile"}
              </h3>
              <div className={styles.s43}>
                <div className={styles.s42}>
                  <h4 className={styles.s40}>
                    {"iPhone / iOS"}
                  </h4>
                  <p className={styles.s41}>
                    {"Download from the Apple App Store."}
                  </p>
                  <a href="https://apps.apple.com/us/app/scw-viewstation-mobile/id6747735805" target="_blank" rel="noopener" className={styles.buttonPrimary}>
                    {"Open App Store"}
                  </a>
                </div>
                <div className={styles.s42}>
                  <h4 className={styles.s40}>
                    {"Android"}
                  </h4>
                  <p className={styles.s41}>
                    {"Download from Google Play."}
                  </p>
                  <a href="https://play.google.com/store/apps/details?id=com.SCWViewstationMobile.mobile&amp;hl=en_US" target="_blank" rel="noopener" className={styles.buttonPrimary}>
                    {"Open Google Play"}
                  </a>
                </div>
              </div>
              <div className={styles.s45}>
                <strong className={styles.s44}>
                  {"First launch:"}
                </strong>
                {" Allow the permissions requested by SCW Viewstation Mobile. You can adjust operating-system permissions later if needed."}
              </div>
            </div>
          </div>
          <h3 className={styles.s47}>
            {"Create an account"}
          </h3>
          <div className={styles.s52}>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"1"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Tap “Log In / Sign Up”"}
                </h4>
                <p className={styles.s50}>
                  {"From the opening screen, select "}
                  <strong className={styles.s44}>
                    {"Log In / Sign Up"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"2"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Enter your email and request a code"}
                </h4>
                <p className={styles.s50}>
                  {"Enter your email address and tap "}
                  <strong className={styles.s44}>
                    {"Send Code"}
                  </strong>
                  {". A mobile phone number is also supported in certain regions. Complete the security verification/CAPTCHA when prompted."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"3"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Verify and set your password"}
                </h4>
                <p className={styles.s50}>
                  {"Enter the code sent to your email, create your login password, read and accept the service agreement and privacy policy, then tap "}
                  <strong className={styles.s44}>
                    {"Sign Up"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.s57}>
            <figure className={styles.s56}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s53} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/c2527f29-scw-viewstation-mobile-security-verification.webp" data-scw-image="scw-viewstation-mobile-security-verification" alt="SCW Viewstation Mobile security verification" />
              </div>
              <figcaption className={styles.s55}>
                {"Complete the security verification when prompted."}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s59} id="add-system">
      <div className={styles.s35}>
        {"Connect your recorder"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"2"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Add your SCW NVR"}
          </h2>
          <p className={styles.s38}>
            {"After login, Viewstation Mobile opens to the Home screen. Add the NVR by scanning its QR code, importing a saved QR-code image, or entering the device register code manually."}
          </p>
          <div className={styles.s52}>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"1"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Tap Add Device"}
                </h4>
                <p className={styles.s50}>
                  {"From the Home screen, select "}
                  <strong className={styles.s44}>
                    {"Add Device"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"2"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Scan the NVR QR code"}
                </h4>
                <p className={styles.s50}>
                  {"Scan the QR code on the "}
                  <strong className={styles.s44}>
                    {"NVR device body"}
                  </strong>
                  {". You can also display the QR code on the NVR’s local monitor under "}
                  <strong className={styles.s44}>
                    {"Network → P2P"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"3"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Use Album or Enter Manually when needed"}
                </h4>
                <p className={styles.s50}>
                  {"Tap "}
                  <strong className={styles.s44}>
                    {"Album"}
                  </strong>
                  {" to import a photo of the QR code, or "}
                  <strong className={styles.s44}>
                    {"Enter Manually"}
                  </strong>
                  {" to enter the device register code."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"4"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Name the system and finish"}
                </h4>
                <p className={styles.s50}>
                  {"Rename the system with the on-screen keyboard and tap "}
                  <strong className={styles.s44}>
                    {"OK"}
                  </strong>
                  {". The system will appear on the Home screen. Tap "}
                  <strong className={styles.s44}>
                    {"Play"}
                  </strong>
                  {" to preview video."}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.s63}>
            <figure className={styles.s62}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s60} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/3a5bd9b8-enter-manually-and-album-options-in-qr-scanner.webp" data-scw-image="enter-manually-and-album-options-in-qr-scanner" alt="Enter Manually and Album options in QR scanner" />
              </div>
              <figcaption className={styles.s61}>
                {"Album and Enter Manually provide alternate setup methods."}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="home-screen">
      <div className={styles.s35}>
        {"Navigation"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"3"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Learn the Home screen"}
          </h2>
          <p className={styles.s38}>
            {HOME_SCREEN_INTRO.slice(0, HOME_SCREEN_INTRO.indexOf('Simple'))}
            <strong className={styles.s44}>
              {"Simple"}
            </strong>
            {HOME_SCREEN_INTRO.slice(HOME_SCREEN_INTRO.indexOf('Simple') + 6, HOME_SCREEN_INTRO.indexOf('Standard'))}
            <strong className={styles.s44}>
              {"Standard"}
            </strong>
            {HOME_SCREEN_INTRO.slice(HOME_SCREEN_INTRO.indexOf('Standard') + 8)}
          </p>
          <div className={styles.s68}>
            <figure className={styles.s65}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s64} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/4e6c335c-annotated-scw-viewstation-mobile-home-screen.webp" data-scw-image="annotated-scw-viewstation-mobile-home-screen" alt="Annotated SCW Viewstation Mobile Home screen" />
              </div>
              <figcaption className={styles.s55}>
                {"Annotated Home screen."}
              </figcaption>
            </figure>
            <div className={styles.s67}>
              <h3 className={styles.s39}>
                {"Home screen controls"}
              </h3>
              <ul className={styles.s66}>
                <li>
                  <strong className={styles.s44}>
                    {"Search Bar:"}
                  </strong>
                  {" Search by device name, serial number, or device model."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Team Name:"}
                  </strong>
                  {" Shows the active team. Tap it to add or switch teams."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Scan:"}
                  </strong>
                  {" Add a device or retrieve a device password."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"App List:"}
                  </strong>
                  {" Displays applications supported by the current team."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Grid / List Mode:"}
                  </strong>
                  {" Switch between a 2-column grid and 1-column list."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Rearrange Devices:"}
                  </strong>
                  {" Drag the device control to change display order."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Simple / Standard:"}
                  </strong>
                  {" Simple mode hides applications."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Group Management:"}
                  </strong>
                  {" Group and manage team devices."}
                </li>
                <li>
                  <strong className={styles.s44}>
                    {"Bottom toolbar:"}
                  </strong>
                  {" Home, Message, Search, and Me/Profile."}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="live-view">
      <div className={styles.s35}>
        {"Video"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"4"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Use Live View and playback"}
          </h2>
          <p className={styles.s38}>
            {"Open a camera to watch live video and use the mobile controls for multi-camera viewing, image adjustment, snapshots, two-way audio, recording, and playback."}
          </p>
          <div className={styles.s73}>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Multiple Windows"}
              </div>
              <p className={styles.s71}>
                {"Load additional cameras into the viewing area. Select the cameras, then tap "}
                <strong className={styles.s44}>
                  {"Play"}
                </strong>
                {"."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Alarm"}
              </div>
              <p className={styles.s71}>
                {"Enable or disable a wired alarm output relay on the NVR or camera to trigger a third-party device."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Image"}
              </div>
              <p className={styles.s71}>
                {"Adjust rotation, brightness, saturation, contrast, sharpness, 2D noise reduction, and 3D noise reduction."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Multi-Window Playback"}
              </div>
              <p className={styles.s71}>
                {"Open playback in a multi-window layout. Tap the "}
                <strong className={styles.s44}>
                  {"+"}
                </strong>
                {" in an empty window to add another camera."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Snapshot"}
              </div>
              <p className={styles.s71}>
                {"Save a still image from the current video to "}
                <strong className={styles.s44}>
                  {"Me → Album"}
                </strong>
                {"."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Microphone"}
              </div>
              <p className={styles.s71}>
                {"Enable two-way audio and use your mobile device to speak through a supported camera speaker."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Record"}
              </div>
              <p className={styles.s71}>
                {"Tap once to begin recording the video you are viewing and again to stop. Saved clips are available under "}
                <strong className={styles.s44}>
                  {"Me → Album"}
                </strong>
                {"."}
              </p>
            </div>
          </div>
          <h3 className={styles.s74}>
            {"Watch: investigation tools"}
          </h3>
          <p className={styles.s75}>
            {"See the investigation workflow in action, including mobile review tools."}
          </p>
          <div className={styles.s78}>
            <button className={styles.s26} type="button" aria-label="Play: SCW Viewstation Mobile investigation tools" data-no-cms-button onClick={() => setActiveVideo('investigation')}>
              <img loading="lazy" decoding="async" className={styles.s22} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/2bf8d85a-scw-viewstation-mobile-video-thumbnail.jpg" data-scw-image="scw-viewstation-mobile-video-thumbnail" alt="SCW Viewstation Mobile video thumbnail" />
              <span className={styles.s25}>
                <span className={styles.s77}>
                  <span className={styles.s76}>

                  </span>
                </span>
              </span>
            </button>
          </div>
          <div className={styles.s79}>
            {"Opens in a full-screen player."}
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="notifications">
      <div className={styles.s35}>
        {"Alerts"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"5"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Configure alarm notifications"}
          </h2>
          <p className={styles.s38}>
            {"Alarm notifications can send push alerts to your mobile device. Viewstation Mobile lets you enable notifications by device and choose which detection/event types should generate alerts."}
          </p>
          <div className={styles.s80}>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"1"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Open Message settings"}
                </h4>
                <p className={styles.s50}>
                  {"Tap "}
                  <strong className={styles.s44}>
                    {"Message"}
                  </strong>
                  {" on the bottom toolbar, then tap the "}
                  <strong className={styles.s44}>
                    {"Gear"}
                  </strong>
                  {" icon in the upper-right corner."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"2"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Allow notifications"}
                </h4>
                <p className={styles.s50}>
                  {"Select "}
                  <strong className={styles.s44}>
                    {"Allow Notifications"}
                  </strong>
                  {", then enable the slider beside the device you want to receive alerts from."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"3"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Choose Push Settings"}
                </h4>
                <p className={styles.s50}>
                  {"Under the device, select "}
                  <strong className={styles.s44}>
                    {"Push Settings"}
                  </strong>
                  {". General Detection is the default tab. Use the "}
                  <strong className={styles.s44}>
                    {"Intrusion"}
                  </strong>
                  {" tab for VCA/smart detection events and the additional options for other device events. Tap "}
                  <strong className={styles.s44}>
                    {"Confirm"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"4"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Review events in Messages"}
                </h4>
                <p className={styles.s50}>
                  {"New events appear in the Messages area. Tap an event to preview it, then choose "}
                  <strong className={styles.s44}>
                    {"Playback"}
                  </strong>
                  {" for recorded video or "}
                  <strong className={styles.s44}>
                    {"Live View"}
                  </strong>
                  {" for current video."}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.s81}>
            <strong className={styles.s44}>
              {"Playback tools:"}
            </strong>
            {" The event timeline marks detection activity. Tap the timeline to jump to a time, use Snapshot for a still image, Recording to capture part of the playback, Speed for fast-forward, Download for the incident clip, and Split to bring additional cameras from the same time range into the review."}
          </div>
          <div className={styles.s63}>
            <figure className={styles.s83}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s82} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/4244b473-messages-screen-with-settings-gear.webp" data-scw-image="messages-screen-with-settings-gear" alt="Messages screen with settings gear" />
              </div>
              <figcaption className={styles.s61}>
                {"Open the Gear from Messages."}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="sharing">
      <div className={styles.s35}>
        {"Multi-user access"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"6"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Share access to your system"}
          </h2>
          <p className={styles.s38}>
            {"The Share function lets you give other users access to devices under your account. You control the permissions and how long the recipient has access."}
          </p>
          <div className={styles.s85}>
            <div>
              <h3 className={styles.s39}>
                {"Account sharing"}
              </h3>
              <ol className={styles.s84}>
                <li>
                  {"Tap the "}
                  <strong className={styles.s44}>
                    {"3 dots"}
                  </strong>
                  {" on the device you want to share."}
                </li>
                <li>
                  {"Select "}
                  <strong className={styles.s44}>
                    {"Shared Use"}
                  </strong>
                  {"."}
                </li>
                <li>
                  {"Set "}
                  <strong className={styles.s44}>
                    {"Share Via"}
                  </strong>
                  {" to "}
                  <strong className={styles.s44}>
                    {"Account"}
                  </strong>
                  {"."}
                </li>
                <li>
                  {"Enter the recipient’s username or email address."}
                </li>
                <li>
                  {"Choose a Sharing Validity Period: "}
                  <strong className={styles.s44}>
                    {"Permanent, 1 Day, 7 Days, 30 Days"}
                  </strong>
                  {", or "}
                  <strong className={styles.s44}>
                    {"Custom"}
                  </strong>
                  {"."}
                </li>
                <li>
                  {"Select "}
                  <strong className={styles.s44}>
                    {"My System"}
                  </strong>
                  {" to choose the recipient’s permissions."}
                </li>
                <li>
                  {"Select "}
                  <strong className={styles.s44}>
                    {"I have read and agree"}
                  </strong>
                  {", then tap "}
                  <strong className={styles.s44}>
                    {"Confirm"}
                  </strong>
                  {"."}
                </li>
              </ol>
            </div>
          </div>
          <div className={styles.s87}>
            <div>
              <h3 className={styles.s39}>
                {"QR code sharing"}
              </h3>
              <ol className={styles.s86}>
                <li>
                  {"Open the device’s "}
                  <strong className={styles.s44}>
                    {"3 dots → Shared Use"}
                  </strong>
                  {"."}
                </li>
                <li>
                  {"Set "}
                  <strong className={styles.s44}>
                    {"Share Via"}
                  </strong>
                  {" to "}
                  <strong className={styles.s44}>
                    {"QR Code"}
                  </strong>
                  {"."}
                </li>
                <li>
                  {"Enter a required "}
                  <strong className={styles.s44}>
                    {"QR Code Name"}
                  </strong>
                  {"."}
                </li>
                <li>
                  {"Set the "}
                  <strong className={styles.s44}>
                    {"QR Code Validity Period"}
                  </strong>
                  {" (how long the QR code can be scanned)."}
                </li>
                <li>
                  {"Set the "}
                  <strong className={styles.s44}>
                    {"Sharing Validity Period"}
                  </strong>
                  {" (how long the user has access)."}
                </li>
                <li>
                  {"Optionally configure an 8-digit "}
                  <strong className={styles.s44}>
                    {"Sharing Verification"}
                  </strong>
                  {" code."}
                </li>
                <li>
                  {"Select "}
                  <strong className={styles.s44}>
                    {"My System"}
                  </strong>
                  {" to choose permissions, accept the agreement, and tap "}
                  <strong className={styles.s44}>
                    {"Confirm"}
                  </strong>
                  {"."}
                </li>
              </ol>
              <div className={styles.s45}>
                <strong className={styles.s44}>
                  {"Registration-free sharing:"}
                </strong>
                {" A recipient can log into Viewstation Mobile as a Guest and scan/import the QR code. "}
                <strong className={styles.s44}>
                  {"Signup-Free Authorization"}
                </strong>
                {" must be enabled on the NVR under "}
                <strong className={styles.s44}>
                  {"Network → P2P"}
                </strong>
                {"."}
              </div>
            </div>
          </div>
          <div className={styles.s57}>
            <figure className={styles.s89}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s88} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/90cf1087-sharing-validity-period-options.webp" data-scw-image="sharing-validity-period-options" alt="Sharing validity period options" />
              </div>
              <figcaption className={styles.s55}>
                {"Set how long access remains active."}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="teams">
      <div className={styles.s35}>
        {"Organization"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"7"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Manage teams"}
          </h2>
          <p className={styles.s38}>
            {"Teams organize users, devices, and applications. A user can belong to multiple teams, including a default team, teams they create, and teams they are invited to join."}
          </p>
          <div className={styles.s52}>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"1"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Create a team"}
                </h4>
                <p className={styles.s50}>
                  {"From Home, tap the "}
                  <strong className={styles.s44}>
                    {"Team Name"}
                  </strong>
                  {" in the upper-left, then tap "}
                  <strong className={styles.s44}>
                    {"+"}
                  </strong>
                  {". You can also add a team from the Message screen by tapping the team name and selecting "}
                  <strong className={styles.s44}>
                    {"Add Team"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"2"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Enter team settings"}
                </h4>
                <p className={styles.s50}>
                  {"Set the "}
                  <strong className={styles.s44}>
                    {"Team Name"}
                  </strong>
                  {", choose a "}
                  <strong className={styles.s44}>
                    {"Team Type"}
                  </strong>
                  {", select the "}
                  <strong className={styles.s44}>
                    {"Time Zone"}
                  </strong>
                  {", then tap "}
                  <strong className={styles.s44}>
                    {"OK"}
                  </strong>
                  {"."}
                </p>
              </div>
            </div>
            <div className={styles.s51}>
              <span className={styles.s48}>
                {"3"}
              </span>
              <div>
                <h4 className={styles.s49}>
                  {"Edit or switch teams"}
                </h4>
                <p className={styles.s50}>
                  {"Use the "}
                  <strong className={styles.s44}>
                    {"Gear"}
                  </strong>
                  {" to rename/edit a team. The same team pages allow you to remove or leave a team. To switch, tap the Team Name and select the team you want loaded on Home."}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.s96}>
            <div className={styles.s91}>
              <div className={styles.s90}>
                {"Team type"}
              </div>
              <div className={styles.s90}>
                {"Support documentation describes it for"}
              </div>
            </div>
            <div className={styles.s94}>
              <div className={styles.s92}>
                {"Enterprise"}
              </div>
              <div className={styles.s93}>
                {"Large-scale security operations such as corporate offices, commercial campuses, and industrial parks, including role-based access control and multiple sub-sites."}
              </div>
            </div>
            <div className={styles.s94}>
              <div className={styles.s92}>
                {"Community"}
              </div>
              <div className={styles.s93}>
                {"Residential complexes, gated neighborhoods, and apartment buildings, with grouping for phases/floors/rooms, resident access, visitors, and QR-code access invitations."}
              </div>
            </div>
            <div className={styles.s95}>
              <div className={styles.s92}>
                {"Family"}
              </div>
              <div className={styles.s93}>
                {"Smart homes and smaller properties, with shared viewing and localized intrusion alerts."}
              </div>
            </div>
          </div>
          <div className={styles.s103}>
            <figure className={styles.s98}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s97} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/c9cf99dc-team-menu-with-add-control.webp" data-scw-image="team-menu-with-add-control" alt="Team menu with add control" />
              </div>
              <figcaption className={styles.s55}>
                {"Tap the team name, then +."}
              </figcaption>
            </figure>
            <figure className={styles.s100}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s99} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/f1fb4a0d-create-team-screen.webp" data-scw-image="create-team-screen" alt="Create Team screen" />
              </div>
              <figcaption className={styles.s55}>
                {"Set name, type, and time zone."}
              </figcaption>
            </figure>
            <figure className={styles.s102}>
              <div className={styles.s54}>
                <img loading="lazy" decoding="async" className={styles.s101} src="https://scw-commerce-media.s3.amazonaws.com/linear/get-68/9031de7a-team-switcher.webp" data-scw-image="team-switcher" alt="Team switcher" />
              </div>
              <figcaption className={styles.s55}>
                {"Select the team you want to load."}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="advanced">
      <div className={styles.s19}>
        {"Advanced administration"}
      </div>
      <h2 className={styles.s20}>
        {"Configure the NVR and individual cameras"}
      </h2>
      <p className={styles.s21}>
        {"Viewstation Mobile provides separate settings for the NVR and for individual camera channels. From Home, tap the device’s "}
        <strong className={styles.s44}>
          {"3 dots → Set"}
        </strong>
        {". Select the NVR at the top for global settings, or select a camera channel for camera-specific settings."}
      </p>
    </section>
    <section className={styles.s69} id="nvr-settings">
      <div className={styles.s35}>
        {"NVR"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"8"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"NVR device configuration"}
          </h2>
          <p className={styles.s104}>
            {"NVR-level controls include system information, time, sharing authorization, storage, and diagnostic tools."}
          </p>
          <div className={styles.s113}>
            <div className={styles.s109}>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('my_system')} data-no-cms-button onClick={() => toggleSection('my_system')}>
                <span>
                  {"My System"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('my_system') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('my_system') ? (
                <div className={styles.s108}>
                  <ul className={styles.s107}>
                    <li>
                      {"Edit the device name."}
                    </li>
                    <li>
                      {"View the firmware version and check for updates."}
                    </li>
                    <li>
                      {"Open mobile push-notification settings with "}
                      <strong className={styles.s44}>
                        {"Allow Alarm Notifications"}
                      </strong>
                      {"."}
                    </li>
                    <li>
                      {"Change the NVR password."}
                    </li>
                    <li>
                      {"Restart the NVR."}
                    </li>
                    <li>
                      {"Transfer NVR ownership to another user or team."}
                    </li>
                    <li>
                      {"Delete the NVR from your account."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div className={styles.s109}>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('time')} data-no-cms-button onClick={() => toggleSection('time')}>
                <span>
                  {"Time"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('time') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('time') ? (
                <div className={styles.s108}>
                  <ul className={styles.s107}>
                    <li>
                      {"Set time manually."}
                    </li>
                    <li>
                      {"Select the time zone."}
                    </li>
                    <li>
                      {"Optionally sync time with your mobile device."}
                    </li>
                    <li>
                      {"Enable or disable Daylight Saving Time (DST)."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div className={styles.s109}>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('share_auth')} data-no-cms-button onClick={() => toggleSection('share_auth')}>
                <span>
                  {"Share and Signup-Free Authorization"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('share_auth') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('share_auth') ? (
                <div className={styles.s111}>
                  <p className={styles.s110}>
                    <strong className={styles.s44}>
                      {"Share"}
                    </strong>
                    {" opens the sharing workflow described above. "}
                    <strong className={styles.s44}>
                      {"Signup-Free Authorization"}
                    </strong>
                    {" allows QR-code sharing to users in the mobile app without requiring an account."}
                  </p>
                </div>
              ) : null}
            </div>
            <div className={styles.s109}>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('storage')} data-no-cms-button onClick={() => toggleSection('storage')}>
                <span>
                  {"Storage"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('storage') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('storage') ? (
                <div className={styles.s108}>
                  <ul className={styles.s107}>
                    <li>
                      <strong className={styles.s44}>
                        {"Overwrite:"}
                      </strong>
                      {" Choose whether the hard drive overwrites old footage when full (default behavior) or stops recording when full."}
                    </li>
                    <li>
                      <strong className={styles.s44}>
                        {"Recording Schedule:"}
                      </strong>
                      {" Configure the global recording calendar."}
                    </li>
                    <li>
                      <strong className={styles.s44}>
                        {"Channel:"}
                      </strong>
                      {" Choose which cameras record; all channels are on by default."}
                    </li>
                    <li>
                      <strong className={styles.s44}>
                        {"Storage Medium:"}
                      </strong>
                      {" View the current capacity of available hard drives."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('more_settings')} data-no-cms-button onClick={() => toggleSection('more_settings')}>
                <span>
                  {"More Settings"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('more_settings') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('more_settings') ? (
                <div className={styles.s112}>
                  <ul className={styles.s107}>
                    <li>
                      <strong className={styles.s44}>
                        {"Export Diagnostic Info:"}
                      </strong>
                      {" Create diagnostic information for troubleshooting with SCW Support."}
                    </li>
                    <li>
                      <strong className={styles.s44}>
                        {"Test Network Speed:"}
                      </strong>
                      {" Run a speed test from the NVR’s network connection."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.s114}>
            <strong className={styles.s44}>
              {"Channel Management:"}
            </strong>
            {" The support documentation states that Channel Management is currently "}
            <strong className={styles.s44}>
              {"not supported by SCW equipment"}
            </strong>
            {"."}
          </div>
          <p className={styles.s115}>
            {"The NVR settings menu also includes Disarm Alarm Linkage; the supplied support documentation does not provide additional configuration steps for that item."}
          </p>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="camera-settings">
      <div className={styles.s35}>
        {"Camera channels"}
      </div>
      <div className={styles.s58}>
        <div className={styles.s36}>
          {"9"}
        </div>
        <div>
          <h2 className={styles.s37}>
            {"Camera device configuration"}
          </h2>
          <p className={styles.s38}>
            {"Select an individual camera channel under "}
            <strong className={styles.s44}>
              {"Set"}
            </strong>
            {" to change that camera’s name, detection rules, image settings, audio, OSD, recording schedule, and PTZ settings."}
          </p>
          <div className={styles.s73}>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Device Info"}
              </div>
              <p className={styles.s71}>
                {"Configure the camera name / primary OSD name."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Share"}
              </div>
              <p className={styles.s71}>
                {"Share a particular camera with a user or team."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Alarm Detection"}
              </div>
              <p className={styles.s71}>
                {"Configure motion detection and VCA/smart features such as intrusion detection, sensitivity, and detection areas."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Customize Alarm Sound"}
              </div>
              <p className={styles.s71}>
                {"For available models, select active-deterrent audio."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Image"}
              </div>
              <p className={styles.s71}>
                {"Set Day/Night mode, WDR, and image parameters such as brightness and saturation."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Sound and Microphone"}
              </div>
              <p className={styles.s71}>
                {"Control microphone input, sound options, and gain."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"OSD"}
              </div>
              <p className={styles.s71}>
                {"Add an additional custom text field. The main camera name is controlled in Device Info."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"Storage"}
              </div>
              <p className={styles.s71}>
                {"Configure the camera’s recording schedule."}
              </p>
            </div>
            <div className={styles.s72}>
              <div className={styles.s70}>
                {"PTZ"}
              </div>
              <p className={styles.s71}>
                {"Configure PTZ presets and patrols."}
              </p>
            </div>
          </div>
          <h3 className={styles.s116}>
            {"Alarm Detection setup"}
          </h3>
          <div className={styles.s117}>
            <div className={styles.s109}>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('motion')} data-no-cms-button onClick={() => toggleSection('motion')}>
                <span>
                  {"Motion Detection"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('motion') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('motion') ? (
                <div className={styles.s108}>
                  <ul className={styles.s107}>
                    <li>
                      {"Enable or disable Motion Detection for the channel."}
                    </li>
                    <li>
                      {"Open "}
                      <strong className={styles.s44}>
                        {"Detection Area"}
                      </strong>
                      {" to view the camera and set a detection zone."}
                    </li>
                    <li>
                      {"Set sensitivity to Low, Medium, or High."}
                    </li>
                    <li>
                      {"Enable "}
                      <strong className={styles.s44}>
                        {"Snapshot"}
                      </strong>
                      {" to include a still image with motion events."}
                    </li>
                    <li>
                      {"Motion Detection runs 24/7 by default. Select "}
                      <strong className={styles.s44}>
                        {"Custom"}
                      </strong>
                      {" under Detection Time to set an active period."}
                    </li>
                    <li>
                      {"Use "}
                      <strong className={styles.s44}>
                        {"Arming Period"}
                      </strong>
                      {" for additional custom active periods."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div className={styles.s109}>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('intrusion')} data-no-cms-button onClick={() => toggleSection('intrusion')}>
                <span>
                  {"Intrusion Detection"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('intrusion') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('intrusion') ? (
                <div className={styles.s108}>
                  <ul className={styles.s107}>
                    <li>
                      {"Enable or disable Intrusion Detection for the channel."}
                    </li>
                    <li>
                      {"Open "}
                      <strong className={styles.s44}>
                        {"Detection Area"}
                      </strong>
                      {", tap "}
                      <strong className={styles.s44}>
                        {"+"}
                      </strong>
                      {", and define the detection shape by selecting its corner points."}
                    </li>
                    <li>
                      {"Set sensitivity to Low, Medium, or High."}
                    </li>
                    <li>
                      <strong className={styles.s44}>
                        {"Stay Time (s)"}
                      </strong>
                      {" controls how long an object must remain in the detection area before an alert is triggered. The support documentation notes that higher values can help rule out shadows, blowing leaves, and other false positives."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div>
              <button className={styles.s106} type="button" aria-expanded={openSections.has('crossline')} data-no-cms-button onClick={() => toggleSection('crossline')}>
                <span>
                  {"Cross Line Detection"}
                </span>
                <span className={styles.s105}>
                  {openSections.has('crossline') ? '–' : '+'}
                </span>
              </button>
              {openSections.has('crossline') ? (
                <div className={styles.s108}>
                  <ul className={styles.s107}>
                    <li>
                      {"Enable or disable Cross Line Detection for the channel."}
                    </li>
                    <li>
                      {"Open "}
                      <strong className={styles.s44}>
                        {"Detection Area"}
                      </strong>
                      {", tap "}
                      <strong className={styles.s44}>
                        {"+"}
                      </strong>
                      {", and place the line across the desired traffic path."}
                    </li>
                    <li>
                      {"Set sensitivity to Low, Medium, or High."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.s69} id="faq">
      <div className={styles.s35}>
        {"Quick answers"}
      </div>
      <h2 className={styles.s118}>
        {"SCW Viewstation Mobile FAQ"}
      </h2>
      <div className={styles.s119}>
        <div className={styles.s109}>
          <button className={styles.s106} type="button" id="question-add-without-scanning" aria-expanded={openSections.has('faq_album')} data-no-cms-button onClick={() => toggleSection('faq_album')}>
            <span>
              {"Can I add my NVR without scanning the QR code live?"}
            </span>
            <span className={styles.s105}>
              {openSections.has('faq_album') ? '–' : '+'}
            </span>
          </button>
          {openSections.has('faq_album') ? (
            <div className={styles.s111}>
              <p className={styles.s110}>
                {"Yes. In the Add Device scanner, tap "}
                <strong className={styles.s44}>
                  {"Album"}
                </strong>
                {" to import a saved QR-code image or "}
                <strong className={styles.s44}>
                  {"Enter Manually"}
                </strong>
                {" to enter the device register code."}
              </p>
            </div>
          ) : null}
        </div>
        <div className={styles.s109}>
          <button className={styles.s106} type="button" id="question-qr-location" aria-expanded={openSections.has('faq_qr_location')} data-no-cms-button onClick={() => toggleSection('faq_qr_location')}>
            <span>
              {"Where is the NVR QR code?"}
            </span>
            <span className={styles.s105}>
              {openSections.has('faq_qr_location') ? '–' : '+'}
            </span>
          </button>
          {openSections.has('faq_qr_location') ? (
            <div className={styles.s111}>
              <p className={styles.s110}>
                {"Use the QR code on the NVR device body or display it on the NVR’s local monitor under "}
                <strong className={styles.s44}>
                  {"Network → P2P"}
                </strong>
                {"."}
              </p>
            </div>
          ) : null}
        </div>
        <div className={styles.s109}>
          <button className={styles.s106} type="button" id="question-alarm-events" aria-expanded={openSections.has('faq_events')} data-no-cms-button onClick={() => toggleSection('faq_events')}>
            <span>
              {"Where do alarm events appear?"}
            </span>
            <span className={styles.s105}>
              {openSections.has('faq_events') ? '–' : '+'}
            </span>
          </button>
          {openSections.has('faq_events') ? (
            <div className={styles.s111}>
              <p className={styles.s110}>
                {"Events appear under "}
                <strong className={styles.s44}>
                  {"Message"}
                </strong>
                {". Tap an event for a preview, then use Playback or Live View as needed."}
              </p>
            </div>
          ) : null}
        </div>
        <div className={styles.s109}>
          <button className={styles.s106} type="button" id="question-share-no-account" aria-expanded={openSections.has('faq_guest')} data-no-cms-button onClick={() => toggleSection('faq_guest')}>
            <span>
              {"Can I share a system with someone who does not have an account?"}
            </span>
            <span className={styles.s105}>
              {openSections.has('faq_guest') ? '–' : '+'}
            </span>
          </button>
          {openSections.has('faq_guest') ? (
            <div className={styles.s111}>
              <p className={styles.s110}>
                {"Yes, using QR-code registration-free sharing. The recipient can use Guest access, and "}
                <strong className={styles.s44}>
                  {"Signup-Free Authorization"}
                </strong>
                {" must be enabled on the NVR under "}
                <strong className={styles.s44}>
                  {"Network → P2P"}
                </strong>
                {"."}
              </p>
            </div>
          ) : null}
        </div>
        <div className={styles.s109}>
          <button className={styles.s106} type="button" id="question-push-notifications" aria-expanded={openSections.has('faq_push')} data-no-cms-button onClick={() => toggleSection('faq_push')}>
            <span>
              {"Why am I not receiving push notifications?"}
            </span>
            <span className={styles.s105}>
              {openSections.has('faq_push') ? '–' : '+'}
            </span>
          </button>
          {openSections.has('faq_push') ? (
            <div className={styles.s111}>
              <p className={styles.s110}>
                {"Confirm that operating-system notifications are allowed for SCW Viewstation Mobile, then go to "}
                <strong className={styles.s44}>
                  {"Message → Gear → Allow Notifications"}
                </strong>
                {" and enable the slider for the desired device. Use "}
                <strong className={styles.s44}>
                  {"Push Settings"}
                </strong>
                {" to choose the event types you want."}
              </p>
            </div>
          ) : null}
        </div>
        <div>
          <button className={styles.s106} type="button" id="question-channel-management" aria-expanded={openSections.has('faq_channel')} data-no-cms-button onClick={() => toggleSection('faq_channel')}>
            <span>
              {"Can I use Channel Management from the mobile app?"}
            </span>
            <span className={styles.s105}>
              {openSections.has('faq_channel') ? '–' : '+'}
            </span>
          </button>
          {openSections.has('faq_channel') ? (
            <div className={styles.s111}>
              <p className={styles.s110}>
                {"The current SCW support documentation states that "}
                <strong className={styles.s44}>
                  {"Channel Management is not supported by SCW equipment"}
                </strong>
                {"."}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
    <section className={styles.s69}>
      <p className={styles.s120}>
        <a href="#top">
          {"Back to top ↑"}
        </a>
      </p>
    </section>
    <section className={styles.s125}>
      <div className={styles.s124}>
        <div className={styles.s121}>
          {"Still need a hand? Talk to a technician."}
        </div>
        <div className={styles.s123}>
          <p className={styles.s122}>
            {"Free US-based support is included for the lifetime of your system. Our technicians are available "}
            <strong className={styles.s44}>
              {"9 AM – 7 PM ET, Mon–Fri"}
            </strong>
            {" by chat, phone, or remote screen-share."}
          </p>
          <div className={styles.s8}>
            <a href="#chat" className={styles.buttonPrimary}>
              {"Chat with support"}
            </a>
            <a href="tel:8664142553" className={styles.buttonSecondary}>
              {"Call 866-414-2553"}
            </a>
          </div>
        </div>
      </div>
    </section>
    {video ? (
      <div className={styles.s135} role="dialog" aria-modal="true" aria-label={video.title} onClick={() => setActiveVideo(null)}>
        <div className={styles.s134} onClick={(event) => event.stopPropagation()}>
          <div className={styles.s130}>
            <div>
              <div className={styles.s126}>
                {video.kicker}
              </div>
              <div className={styles.s127}>
                {video.title}
              </div>
            </div>
            <button className={styles.s129} type="button" aria-label="Close video" data-no-cms-button onClick={() => setActiveVideo(null)}>
              <span className={styles.s128}>
                {"×"}
              </span>
              <span>
                {"Close"}
              </span>
            </button>
          </div>
          <div className={styles.s132}>
            <iframe className={styles.s131} src={video.src} title={video.title} allow={'accelerometer; autoplay; clip' + 'board-write; encrypted-' + 'media; picture-in-picture'} allowFullScreen>

            </iframe>
          </div>
          <div className={styles.s133}>
            {"Press Esc or click outside the video to close."}
          </div>
        </div>
      </div>
    ) : null}
  </article>
  );
}
